import { getAddress } from '@ethersproject/address'
import { BigNumber, formatFixed, parseFixed } from '@ethersproject/bignumber'
import { WeiPerEther as ONE } from '@ethersproject/constants'
import cloneDeep from 'lodash.clonedeep'

import { PoolBase, PoolTypes, SubgraphPoolBase, SubgraphToken, SwapTypes } from '../../../../types/balancer'
import { isSameAddress } from '../../../../utils/balancer'
import { bnum, BigNumber as OldBigNumber, ZERO } from '../../../../utils/balancer/bignumber'
import { universalNormalizedLiquidity } from '../liquidity'
import { StablePoolPairData } from '../stablePool'
import {
  _calcBptInGivenExactTokensOut,
  _calcBptOutGivenExactTokensIn,
  _calcInGivenOut,
  _calcOutGivenIn,
  _calcTokenInGivenExactBptOut,
  _calcTokenOutGivenExactBptIn,
} from '../stablePool/stableMathBigInt'
import * as phantomStableMath from './phantomStableMath'

export enum PairTypes {
  BptToToken,
  TokenToBpt,
  TokenToToken,
}

export type PhantomStablePoolToken = Pick<SubgraphToken, 'address' | 'balance' | 'decimals' | 'priceRate'>

export type MetaStablePoolPairData = StablePoolPairData & {
  tokenInPriceRate: BigNumber
  tokenOutPriceRate: BigNumber
}

export type PhantomStablePoolPairData = MetaStablePoolPairData & {
  pairType: PairTypes
  bptIndex: number
  virtualBptSupply: BigNumber
}

export class PhantomStablePool implements PoolBase<PhantomStablePoolPairData> {
  poolType: PoolTypes = PoolTypes.MetaStable
  id: string
  address: string
  amp: BigNumber
  swapFee: BigNumber
  totalShares: BigNumber
  tokens: PhantomStablePoolToken[]
  tokensList: string[]
  ALMOST_ONE = parseFixed('0.99', 18)

  static AMP_DECIMALS = 3

  static fromPool(pool: SubgraphPoolBase): PhantomStablePool {
    if (!pool.amp) throw new Error('PhantomStablePool missing amp factor')
    return new PhantomStablePool(
      pool.id,
      pool.address,
      pool.amp,
      pool.swapFee,
      pool.totalShares,
      pool.tokens,
      pool.tokensList
    )
  }

  // Remove BPT from Balances and update indices
  static removeBPT(poolPairData: PhantomStablePoolPairData): PhantomStablePoolPairData {
    const poolPairDataNoBpt = cloneDeep(poolPairData)
    const bptIndex = poolPairData.bptIndex
    if (bptIndex != -1) {
      poolPairDataNoBpt.allBalances.splice(bptIndex, 1)
      poolPairDataNoBpt.allBalancesScaled.splice(bptIndex, 1)
      if (bptIndex < poolPairData.tokenIndexIn) poolPairDataNoBpt.tokenIndexIn -= 1
      if (bptIndex < poolPairData.tokenIndexOut) poolPairDataNoBpt.tokenIndexOut -= 1
    }
    return poolPairDataNoBpt
  }

  constructor(
    id: string,
    address: string,
    amp: string,
    swapFee: string,
    totalShares: string,
    tokens: PhantomStablePoolToken[],
    tokensList: string[]
  ) {
    this.id = id
    this.address = address
    this.amp = BigNumber.from(amp)
    this.swapFee = BigNumber.from(swapFee)
    this.totalShares = BigNumber.from(totalShares)
    this.tokens = tokens
    this.tokensList = tokensList
  }

  parsePoolPairData(tokenIn: string, tokenOut: string): PhantomStablePoolPairData {
    const tokenIndexIn = this.tokens.findIndex((t) => getAddress(t.address) === getAddress(tokenIn))
    if (tokenIndexIn < 0) throw 'Pool does not contain tokenIn'
    const tI = this.tokens[tokenIndexIn]
    const balanceIn = bnum(tI.balance).times(bnum(tI.priceRate)).dp(tI.decimals).toString()
    const decimalsIn = tI.decimals
    const tokenInPriceRate = parseFixed(tI.priceRate, 18)

    const tokenIndexOut = this.tokens.findIndex((t) => getAddress(t.address) === getAddress(tokenOut))
    if (tokenIndexOut < 0) throw 'Pool does not contain tokenOut'
    const tO = this.tokens[tokenIndexOut]
    const balanceOut = bnum(tO.balance).times(bnum(tO.priceRate)).dp(tO.decimals).toString()
    const decimalsOut = tO.decimals
    const tokenOutPriceRate = parseFixed(tO.priceRate, 18)

    // Get all token balances
    const allBalances = this.tokens.map(({ balance, priceRate }) => bnum(balance).times(bnum(priceRate)))
    const allBalancesScaled = this.tokens.map(({ balance, priceRate }) =>
      parseFixed(balance, 18).mul(parseFixed(priceRate, 18)).div(ONE)
    )

    // Phantom pools allow trading between token and pool BPT
    let pairType: PairTypes
    if (isSameAddress(tokenIn, this.address)) {
      pairType = PairTypes.BptToToken
    } else if (isSameAddress(tokenOut, this.address)) {
      pairType = PairTypes.TokenToBpt
    } else {
      pairType = PairTypes.TokenToToken
    }

    const bptIndex = this.tokensList.indexOf(this.address)

    // VirtualBPTSupply must be used for the maths
    const virtualBptSupply = this.totalShares

    const poolPairData: PhantomStablePoolPairData = {
      id: this.id,
      address: this.address,
      poolType: this.poolType,
      pairType,
      bptIndex,
      tokenIn,
      tokenOut,
      balanceIn: parseFixed(balanceIn, decimalsIn),
      balanceOut: parseFixed(balanceOut, decimalsOut),
      swapFee: this.swapFee,
      allBalances,
      allBalancesScaled,
      amp: this.amp,
      tokenIndexIn,
      tokenIndexOut,
      decimalsIn: Number(decimalsIn),
      decimalsOut: Number(decimalsOut),
      tokenInPriceRate,
      tokenOutPriceRate,
      virtualBptSupply,
    }

    return PhantomStablePool.removeBPT(poolPairData)
  }

  getNormalizedLiquidity(poolPairData: PhantomStablePoolPairData): OldBigNumber {
    return universalNormalizedLiquidity(this._derivativeSpotPriceAfterSwapExactTokenInForTokenOut(poolPairData, ZERO))
  }

  getLimitAmountSwap(poolPairData: PhantomStablePoolPairData, swapType: SwapTypes): OldBigNumber {
    // PoolPairData is using balances that have already been exchanged so need to convert back
    if (swapType === SwapTypes.SwapExactIn) {
      // Return max valid amount of tokenIn
      // As an approx - use almost the total balance of token out as we can add any amount of tokenIn and expect some back
      return bnum(
        formatFixed(
          poolPairData.balanceOut.mul(this.ALMOST_ONE).div(poolPairData.tokenOutPriceRate),
          poolPairData.decimalsOut
        )
      )
    } else {
      // Return max amount of tokenOut - approx is almost all balance
      return bnum(
        formatFixed(
          poolPairData.balanceOut.mul(this.ALMOST_ONE).div(poolPairData.tokenOutPriceRate),
          poolPairData.decimalsOut
        )
      )
    }
  }

  // Updates the balance of a given token for the pool
  updateTokenBalanceForPool(token: string, newBalance: BigNumber): void {
    // token is underlying in the pool
    const T = this.tokens.find((t) => isSameAddress(t.address, token))
    if (!T) throw Error('Pool does not contain this token')

    // update total shares with BPT balance diff
    if (isSameAddress(this.address, token)) {
      const parsedTokenBalance = parseFixed(T.balance, T.decimals)
      const diff = parsedTokenBalance.sub(newBalance)
      const newTotalShares = this.totalShares.add(diff)
      this.updateTotalShares(newTotalShares)
    }
    // update token balance with new balance
    T.balance = formatFixed(newBalance, T.decimals)
  }

  updateTotalShares(newTotalShares: BigNumber): void {
    this.totalShares = newTotalShares
  }

  _exactTokenInForTokenOut(poolPairData: PhantomStablePoolPairData, amount: OldBigNumber): OldBigNumber {
    try {
      // This code assumes that decimalsIn and decimalsOut is 18

      if (amount.isZero()) return ZERO
      // All values should use 1e18 fixed point
      // i.e. 1USDC => 1e18 not 1e6
      // In Phantom Pools every time there is a swap (token per token, bpt per token or token per bpt), we substract the fee from the amount in
      const amtWithFeeEvm = this.subtractSwapFeeAmount(parseFixed(amount.dp(18).toString(), 18), poolPairData.swapFee)
      const amountConvertedEvm = amtWithFeeEvm.mul(poolPairData.tokenInPriceRate).div(ONE)

      let returnEvm: bigint

      if (poolPairData.pairType === PairTypes.TokenToBpt) {
        const amountsInBigInt = Array(poolPairData.allBalancesScaled.length).fill(BigInt(0))
        amountsInBigInt[poolPairData.tokenIndexIn] = amountConvertedEvm.toBigInt()

        returnEvm = _calcBptOutGivenExactTokensIn(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          amountsInBigInt,
          poolPairData.virtualBptSupply.toBigInt(),
          BigInt(0)
        )
      } else if (poolPairData.pairType === PairTypes.BptToToken) {
        returnEvm = _calcTokenOutGivenExactBptIn(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          poolPairData.tokenIndexOut,
          amountConvertedEvm.toBigInt(),
          poolPairData.virtualBptSupply.toBigInt(),
          BigInt(0)
        )
      } else {
        returnEvm = _calcOutGivenIn(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          poolPairData.tokenIndexIn,
          poolPairData.tokenIndexOut,
          amountConvertedEvm.toBigInt(),
          BigInt(0)
        )
      }

      const returnEvmWithRate = BigNumber.from(returnEvm).mul(ONE).div(poolPairData.tokenOutPriceRate)

      // Return human scaled
      return bnum(formatFixed(returnEvmWithRate, 18))
    } catch (err) {
      // console.error(`PhantomStable _evmoutGivenIn: ${err.message}`);
      return ZERO
    }
  }

  _tokenInForExactTokenOut(poolPairData: PhantomStablePoolPairData, amount: OldBigNumber): OldBigNumber {
    try {
      // This code assumes that decimalsIn and decimalsOut is 18

      if (amount.isZero()) return ZERO
      // All values should use 1e18 fixed point
      // i.e. 1USDC => 1e18 not 1e6
      const amountConvertedEvm = parseFixed(amount.dp(18).toString(), 18).mul(poolPairData.tokenOutPriceRate).div(ONE)

      let returnEvm: bigint

      if (poolPairData.pairType === PairTypes.TokenToBpt) {
        returnEvm = _calcTokenInGivenExactBptOut(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          poolPairData.tokenIndexIn,
          amountConvertedEvm.toBigInt(),
          poolPairData.virtualBptSupply.toBigInt(),
          BigInt(0)
        )
      } else if (poolPairData.pairType === PairTypes.BptToToken) {
        const amountsOutBigInt = Array(poolPairData.allBalancesScaled.length).fill(BigInt(0))
        amountsOutBigInt[poolPairData.tokenIndexOut] = amountConvertedEvm.toBigInt()

        returnEvm = _calcBptInGivenExactTokensOut(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          amountsOutBigInt,
          poolPairData.virtualBptSupply.toBigInt(),
          BigInt(0) // Fee is handled below
        )
      } else {
        returnEvm = _calcInGivenOut(
          this.amp.toBigInt(),
          poolPairData.allBalancesScaled.map((b) => b.toBigInt()),
          poolPairData.tokenIndexIn,
          poolPairData.tokenIndexOut,
          amountConvertedEvm.toBigInt(),
          BigInt(0) // Fee is handled below
        )
      }
      // In Phantom Pools every time there is a swap (token per token, bpt per token or token per bpt), we substract the fee from the amount in
      const returnEvmWithRate = BigNumber.from(returnEvm).mul(ONE).div(poolPairData.tokenInPriceRate)

      const returnEvmWithFee = this.addSwapFeeAmount(returnEvmWithRate, poolPairData.swapFee)

      // return human number
      return bnum(formatFixed(returnEvmWithFee, 18))
    } catch (err) {
      // console.error(`PhantomStable _evminGivenOut: ${err.message}`)
      return ZERO
    }
  }

  /**
   * _calcTokensOutGivenExactBptIn
   * @param _bptAmountIn EVM scale.
   * @returns EVM scale.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _calcTokensOutGivenExactBptIn(_bptAmountIn: BigNumber): BigNumber[] {
    // PhantomStables can only be exited by using BPT > token swaps
    throw new Error('PhantomPool does not have exit pool (_calcTokensOutGivenExactBptIn).')
  }

  /**
   * _calcBptOutGivenExactTokensIn
   * @param _amountsIn EVM Scale
   * @returns EVM Scale
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _calcBptOutGivenExactTokensIn(_amountsIn: BigNumber[]): BigNumber {
    // PhantomStables can only be joined by using token > BPT swaps
    throw new Error('PhantomPool does not have join pool (_calcBptOutGivenExactTokensIn).')
  }

  // this is the multiplicative inverse of the derivative of _exactTokenInForTokenOut
  _spotPriceAfterSwapExactTokenInForTokenOut(
    poolPairData: PhantomStablePoolPairData,
    amount: OldBigNumber
  ): OldBigNumber {
    const priceRateIn = bnum(formatFixed(poolPairData.tokenInPriceRate, 18))
    const priceRateOut = bnum(formatFixed(poolPairData.tokenOutPriceRate, 18))
    const amountConverted = amount.times(bnum(formatFixed(poolPairData.tokenInPriceRate, 18)))
    let result: OldBigNumber
    if (poolPairData.pairType === PairTypes.TokenToBpt) {
      result = phantomStableMath._spotPriceAfterSwapExactTokenInForBPTOut(amountConverted, poolPairData)
    } else if (poolPairData.pairType === PairTypes.BptToToken) {
      result = phantomStableMath._spotPriceAfterSwapExactBPTInForTokenOut(amountConverted, poolPairData)
    } else {
      result = phantomStableMath._spotPriceAfterSwapExactTokenInForTokenOut(amountConverted, poolPairData)
    }
    return result.div(priceRateIn).times(priceRateOut)
  }

  // this is the multiplicative inverse of the derivative of _exactTokenInForTokenOut
  _spotPriceExactTokenInForTokenOut(poolPairData: PhantomStablePoolPairData): OldBigNumber {
    const priceRateIn = bnum(formatFixed(poolPairData.tokenInPriceRate, 18))
    const priceRateOut = bnum(formatFixed(poolPairData.tokenOutPriceRate, 18))
    let result: OldBigNumber
    if (poolPairData.pairType === PairTypes.TokenToBpt) {
      result = phantomStableMath._spotPriceAfterSwapExactTokenInForBPTOut(ZERO, poolPairData)
    } else if (poolPairData.pairType === PairTypes.BptToToken) {
      result = phantomStableMath._spotPriceAfterSwapExactBPTInForTokenOut(ZERO, poolPairData)
    } else {
      result = phantomStableMath._spotPriceAfterSwapExactTokenInForTokenOut(ZERO, poolPairData)
    }
    return result.div(priceRateIn).times(priceRateOut)
  }

  // this is the derivative of _tokenInForExactTokenOut
  _spotPriceAfterSwapTokenInForExactTokenOut(
    poolPairData: PhantomStablePoolPairData,
    amount: OldBigNumber
  ): OldBigNumber {
    const priceRateIn = bnum(formatFixed(poolPairData.tokenInPriceRate, 18))
    const priceRateOut = bnum(formatFixed(poolPairData.tokenOutPriceRate, 18))
    const amountConverted = amount.times(formatFixed(poolPairData.tokenOutPriceRate, 18))
    let result: OldBigNumber
    if (poolPairData.pairType === PairTypes.TokenToBpt) {
      result = phantomStableMath._spotPriceAfterSwapTokenInForExactBPTOut(amountConverted, poolPairData)
    } else if (poolPairData.pairType === PairTypes.BptToToken) {
      result = phantomStableMath._spotPriceAfterSwapBPTInForExactTokenOut(amountConverted, poolPairData)
    } else {
      result = phantomStableMath._spotPriceAfterSwapTokenInForExactTokenOut(amountConverted, poolPairData)
    }
    return result.div(priceRateIn).times(priceRateOut)
  }

  _derivativeSpotPriceAfterSwapExactTokenInForTokenOut(
    poolPairData: PhantomStablePoolPairData,
    amount: OldBigNumber
  ): OldBigNumber {
    const priceRateOut = bnum(formatFixed(poolPairData.tokenOutPriceRate, 18))
    const amountConverted = amount.times(formatFixed(poolPairData.tokenInPriceRate, 18))
    let result: OldBigNumber
    if (poolPairData.pairType === PairTypes.TokenToBpt) {
      result = phantomStableMath._derivativeSpotPriceAfterSwapExactTokenInForBPTOut(amountConverted, poolPairData)
    } else if (poolPairData.pairType === PairTypes.BptToToken) {
      result = phantomStableMath._derivativeSpotPriceAfterSwapExactBPTInForTokenOut(amountConverted, poolPairData)
    } else {
      result = phantomStableMath._derivativeSpotPriceAfterSwapExactTokenInForTokenOut(amountConverted, poolPairData)
    }
    return result.times(priceRateOut)
  }

  _derivativeSpotPriceAfterSwapTokenInForExactTokenOut(
    poolPairData: PhantomStablePoolPairData,
    amount: OldBigNumber
  ): OldBigNumber {
    const priceRateIn = bnum(formatFixed(poolPairData.tokenInPriceRate, 18))
    const priceRateOut = bnum(formatFixed(poolPairData.tokenOutPriceRate, 18))
    const amountConverted = amount.times(formatFixed(poolPairData.tokenOutPriceRate, 18))
    let result: OldBigNumber
    if (poolPairData.pairType === PairTypes.TokenToBpt) {
      result = phantomStableMath._derivativeSpotPriceAfterSwapTokenInForExactBPTOut(amountConverted, poolPairData)
    } else if (poolPairData.pairType === PairTypes.BptToToken) {
      result = phantomStableMath._derivativeSpotPriceAfterSwapBPTInForExactTokenOut(amountConverted, poolPairData)
    } else {
      result = phantomStableMath._derivativeSpotPriceAfterSwapTokenInForExactTokenOut(amountConverted, poolPairData)
    }
    return result.div(priceRateIn).times(priceRateOut).times(priceRateOut)
  }

  subtractSwapFeeAmount(amount: BigNumber, swapFee: BigNumber): BigNumber {
    // https://github.com/balancer-labs/balancer-v2-monorepo/blob/c18ff2686c61a8cbad72cdcfc65e9b11476fdbc3/pkg/pool-utils/contracts/BasePool.sol#L466
    const feeAmount = amount.mul(swapFee).add(ONE.sub(1)).div(ONE)
    return amount.sub(feeAmount)
  }

  addSwapFeeAmount(amount: BigNumber, swapFee: BigNumber): BigNumber {
    // https://github.com/balancer-labs/balancer-v2-monorepo/blob/c18ff2686c61a8cbad72cdcfc65e9b11476fdbc3/pkg/pool-utils/contracts/BasePool.sol#L458
    const feeAmount = ONE.sub(swapFee)
    return amount.mul(ONE).add(feeAmount.sub(1)).div(feeAmount)
  }
}
